import React, { useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router';
import queryString from 'query-string';

import { Loader, LoadingOverlay } from '@mantine/core';

import { MenuManagementProvider } from 'context/MenuManagementProvider';
import { useGetCateringSettings } from 'api/location/useGetCateringSettings';
import { isEmpty } from 'helpers/utils';
import { useAuth } from 'hooks/useAuth';
import { useMenuManagement } from 'hooks/useMenuManagement';
import { ErrorBoundaryError } from 'errorBoundary/errors';

import { useHasCateringScope } from 'hooks/useHasCateringScope';
import MenuEdit from 'components/MenuManagement/MenuEdit/MenuEdit';
import { MenuContent } from 'components/MenuManagement/MenuEdit/MenuContent/MenuContent';
import { useMenuListPath } from 'components/MenuManagement/hooks/useMenuListPath';
import { ERROR_ACTION } from 'constants/error';

export default function MenuEditCreate() {
  const { user: chownowUser, cubohUser } = useAuth();

  if (!chownowUser || !cubohUser) return <Loader />;

  return (
    <MenuManagementProvider>
      <EditCreateContent />
    </MenuManagementProvider>
  );
}

function EditCreateContent() {
  const { merchants } = useAuth();
  const { menuEditID, setMenuEditID, createMenu, setCreateMenu, singleMenu, getSingleCubohMenu } =
    useMenuManagement();
  const navigate = useNavigate();
  const location = useLocation();
  const { restaurantId } = useParams();
  const hasCateringScope = useHasCateringScope();
  const { cateringData, isCateringLoading } = useGetCateringSettings(
    restaurantId,
    hasCateringScope,
  );
  const menuListPath = useMenuListPath();
  const queryParams = queryString.parse(location.search);
  // menus can name a merchant this user's auth payload doesn't carry
  const merchantCompanyID = merchants?.[queryParams.merchantId]?.companyID;

  useEffect(() => {
    if (queryParams.merchantId) {
      if (queryParams.menuId && merchantCompanyID) {
        const shouldLoadMenu =
          !menuEditID ||
          menuEditID.menuUuid !== queryParams.menuId ||
          menuEditID.merchantID !== queryParams.merchantId;

        if (shouldLoadMenu) {
          setMenuEditID({ menuUuid: queryParams.menuId, merchantID: queryParams.merchantId });
          getSingleCubohMenu(merchantCompanyID, queryParams.merchantId, queryParams.menuId);
        }
      }

      if (!queryParams.menuId && !createMenu) {
        setCreateMenu(queryParams.merchantId);
      }
    } else {
      navigate(menuListPath);
    }
  }, [queryParams, merchantCompanyID, menuListPath, navigate]);

  if (
    isEmpty(queryParams) ||
    (queryParams.menuId && merchantCompanyID && isEmpty(singleMenu?.data)) ||
    isCateringLoading
  ) {
    return <LoadingOverlay visible />;
  }

  // only check for menu data if we're in edit mode otherwise create menu page won't show
  if (queryParams.menuId && !singleMenu?.data?.[queryParams.merchantId]?.[0]) {
    throw new ErrorBoundaryError({
      hasError: true,
      errorAction: ERROR_ACTION.SHOW_ERROR_MODAL,
      errorDetails: {
        title: 'Menu not found',
        message: 'There was an error loading your menu. Please try again.',
      },
    });
  }

  return (
    <MenuEdit
      setMenuEditID={setMenuEditID}
      menu={singleMenu?.data?.[queryParams.merchantId]?.[0]}
      merchantID={queryParams.merchantId}
      createMenu={createMenu}
      setCreateMenu={setCreateMenu}
      cateringData={cateringData}
    >
      <MenuContent />
    </MenuEdit>
  );
}
